import { useEffect, useRef } from 'react';
import { Tag, User, Severity } from '../CreateTask/create_task/types';
import { priorities } from '../CreateTask/create_task/right_panel/severity';

type Props = {
  availableTags?: Tag[];
  onTagsChange?: (tags: Tag[]) => void;
  assignableUsers?: User[];
  onAssigneesChange?: (assignees: number[]) => void;
  onSeverityChange?: (severity: Severity) => void;
  createTagsViaNewTask?: boolean;
};

const useConfigDefaultsInCreateTask = ({
  availableTags,
  onTagsChange,
  assignableUsers,
  onAssigneesChange,
  onSeverityChange,
  createTagsViaNewTask = false
}: Props) => {
  const w: any = window;
  const config = (w && w.BugHerdConfig) || undefined;
  const tags = config?.tags;
  const assignees = config?.assignees;
  const severity = config?.severity;

  const initialiseDefaultTags = () => {
    if (!tags?.length) return;
    if (!onTagsChange) return;

    const preset: Tag[] = [];

    if (availableTags?.length) {
      availableTags.forEach((value: Tag) => {
        if (tags.find?.((configTag: string) => configTag === value.display)) {
          preset.push(value);
        }
      });
    }

    if (createTagsViaNewTask) {
      const existingDisplays = new Set(preset.map(t => t.display));
      tags.forEach((configTag: string) => {
        if (!existingDisplays.has(configTag)) {
          const newTag: Tag = { id: -1, display: configTag };
          preset.push(newTag);
        }
      });
    }

    if (preset.length && onTagsChange) onTagsChange(preset);
  };

  const initialiseDefaultAssignees = () => {
    if (assignableUsers?.length) {
      const presetAssignees = assignableUsers.reduce(
        (result: number[], value: User) => {
          if (assignees?.find?.((assignee: string) => assignee === value.email))
            result.push(value.id);
          return result;
        },
        []
      );
      if (onAssigneesChange) onAssigneesChange(presetAssignees);
    }
  };

  const initialiseDefaultSeverity = () => {
    if (severity) {
      const presetSeverity = priorities.find(({ name }) => name === severity);
      if (presetSeverity && onSeverityChange)
        onSeverityChange(presetSeverity.id);
    }
  };

  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    const haveTagsInfo =
      (availableTags && availableTags.length > 0) ||
      (!!tags?.length && !!createTagsViaNewTask);
    const haveAssigneesInfo = assignableUsers && assignableUsers.length > 0;

    if (haveTagsInfo || haveAssigneesInfo || severity) {
      initialiseDefaultTags();
      initialiseDefaultAssignees();
      initialiseDefaultSeverity();
      initializedRef.current = true;
    }
  }, [availableTags, assignableUsers, severity, createTagsViaNewTask]);
};

export default useConfigDefaultsInCreateTask;
